/**
 * PricingRuleCreatePage — consolidated wizard at /inventory/pricing-rule/new.
 *
 * One flow builds any storefront promotion. Pick a reward (discount or free
 * item), choose what it applies to, add perks (free shipping / embroidery),
 * set conditions, then optionally flip "Make it a coupon code" to turn the same
 * config into a redeemable coupon.
 *
 *   coupon OFF → plain automatic Pricing Rule   (pricingRules.create)
 *   coupon ON  → Pricing Rule + Coupon Code pair (admin_coupon.create_promotion)
 *
 * See docs plan 1936 for the capability→field map.
 */

import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useLanguage } from "@/i18n/useLanguage";
import { pricingRules } from "@/lib/erp/pricingRules";
import { promotionApi } from "@/features/promotions/api/promotionApi";
import { CARD } from "@/config/design-tokens";
import { PreviewPanel, type PreviewState } from "./PricingRuleCreatePreview";
import { RewardPicker, SectionLabel } from "./PricingRuleCreateWidgets";
import {
  DiscountConfig,
  FreeItemConfig,
  ScopeSection,
  PerksSection,
  ConditionsSection,
  CouponSection,
  ConditionalCustomizationSection,
  StorefrontVisibilitySection,
} from "./PricingRuleCreateSections";
import {
  INITIAL,
  labels,
  hasBenefit,
  buildPricingRulePayload,
  buildCouponParams,
  type WizardState,
} from "./pricingRuleWizardModel";
import { ChevronRight } from "lucide-react";

export function PricingRuleCreatePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [s, setS] = useState<WizardState>(INITIAL);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = useCallback(
    (key: string) => {
      const entry = labels[key];
      return entry ? (language === "th" ? entry.th : entry.en) : key;
    },
    [language],
  );

  const set = useCallback(<K extends keyof WizardState>(k: K, v: WizardState[K]) => {
    setS((p) => ({ ...p, [k]: v }));
  }, []);

  const sectionProps = { s, set, lang: language, t };

  // Progress steps: reward → applies-to → conditions → (name/coupon)
  const rewardDone = s.rewardKind === "product" ? s.freeQty > 0 : !!s.discountMode && s.discountValue > 0;
  const steps = [rewardDone, !!s.applyOn, !!s.title, s.isCoupon ? !!s.couponCode : true];
  const completed = steps.filter(Boolean).length;

  // Preview reuses the existing type-specific panels
  const previewState: PreviewState = useMemo(
    () => ({
      ruleType: s.isCoupon ? "coupon" : s.rewardKind === "product" ? "product" : "price",
      title: s.title,
      discountMode: s.discountMode,
      discountValue: s.discountValue,
      sameItem: s.sameItem,
      freeItem: s.freeItem,
      freeQty: s.freeQty,
      applyOn: s.applyOn,
      minQty: s.minQty,
      minAmt: s.minAmt,
      validUpto: s.validUpto,
    }),
    [s.isCoupon, s.rewardKind, s.title, s.discountMode, s.discountValue, s.sameItem, s.freeItem, s.freeQty, s.applyOn, s.minQty, s.minAmt, s.validUpto],
  );

  const validate = (): string | null => {
    if (!s.title.trim()) return language === "th" ? "กรุณาตั้งชื่อโปรโมชั่น" : "Please name the promotion";
    if (!hasBenefit(s))
      return language === "th"
        ? "โปรโมชั่นต้องให้สิทธิประโยชน์อย่างน้อยหนึ่งอย่าง"
        : "Add at least one benefit (discount, free item, free shipping or embroidery)";
    if (s.applyOn !== "Transaction" && !s.scopeValue)
      return language === "th" ? "เลือกสินค้า/หมวดหมู่/แบรนด์ที่จะใช้" : "Select the item, category or brand it applies to";
    if (s.isCoupon) {
      if (!s.couponCode.trim()) return language === "th" ? "กรุณาใส่โค้ดคูปอง" : "Enter a coupon code";
      if (!s.validFrom || !s.validUpto) return language === "th" ? "คูปองต้องมีวันเริ่มและวันสิ้นสุด" : "Coupons need a start and end date";
    }
    return null;
  };

  const handleCreate = async () => {
    const err = validate();
    if (err) {
      setError(err);
      toast.error(err);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      if (s.isCoupon) {
        const res = await promotionApi.create(buildCouponParams(s) as never);
        toast.success(language === "th" ? "สร้างคูปองสำเร็จ" : "Coupon created!");
        navigate(`/promotions/${encodeURIComponent(res.coupon_code)}`);
      } else {
        const res = await pricingRules.create(buildPricingRulePayload(s));
        toast.success(language === "th" ? "สร้างกฎราคาสำเร็จ" : "Pricing rule created!");
        navigate(`/inventory/pricing-rule/${res.name}`);
      }
    } catch (e: unknown) {
      const msg = (e as Error)?.message || "Failed to create";
      setError(msg);
      toast.error(msg);
      setIsSaving(false);
    }
  };

  const isDiscount = s.rewardKind === "discount";

  return (
    <div className="min-h-screen bg-background py-10 font-sans">
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">{t("page_title")}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t("page_desc")}</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              {steps.map((done, i) => (
                <div key={i} className={cn("w-8 h-1.5 rounded-full transition-all duration-300", done ? "bg-primary" : "bg-muted")} />
              ))}
            </div>
            <span className="text-xs text-muted-foreground tabular-nums ml-1">{completed}/{steps.length}</span>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-[1fr_300px] gap-10">
          <div className="space-y-8">
            {/* 1. Reward */}
            <div className="space-y-3">
              <SectionLabel>{t("reward_header")}</SectionLabel>
              <RewardPicker value={s.rewardKind} onChange={(rk) => set("rewardKind", rk)} lang={language} />
              {isDiscount ? <DiscountConfig {...sectionProps} /> : <FreeItemConfig {...sectionProps} />}
            </div>

            {/* 2. Applies to */}
            <div className="space-y-3">
              <SectionLabel>{t("scope_header")}</SectionLabel>
              <ScopeSection {...sectionProps} />
            </div>

            {/* 3. Perks */}
            <div className="space-y-3">
              <SectionLabel>{t("perks_header")}</SectionLabel>
              <PerksSection {...sectionProps} />
            </div>

            {/* 4. Promotion name */}
            <div className="space-y-3">
              <SectionLabel>{t("name_header")}</SectionLabel>
              <Card className={CARD}>
                <CardContent className="p-5">
                  <Input value={s.title} onChange={(e) => set("title", e.target.value)} placeholder={t("name_placeholder")} className="h-10" />
                </CardContent>
              </Card>
            </div>

            {/* 5. Conditions */}
            <div className="space-y-3">
              <SectionLabel>{t("conditions_header")}</SectionLabel>
              <ConditionsSection {...sectionProps} />
            </div>

            {/* 6. Make it a coupon */}
            <div className="space-y-3">
              <SectionLabel>{t("coupon_header")}</SectionLabel>
              <CouponSection {...sectionProps} />
            </div>

            {/* 6b. Conditional customization (Group / Bulk Deal — coupon-gated) */}
            {s.isCoupon && (
              <div className="space-y-3">
                <SectionLabel>{t("conditional_header")}</SectionLabel>
                <ConditionalCustomizationSection {...sectionProps} />
              </div>
            )}

            {/* 7. Storefront visibility (automatic rules only) */}
            {!s.isCoupon && (
              <div className="space-y-3">
                <SectionLabel>{t("storefront_header")}</SectionLabel>
                <StorefrontVisibilitySection {...sectionProps} />
              </div>
            )}

            {/* Create */}
            <div className="pt-2 pb-10 space-y-2">
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button className="w-full h-11 rounded text-sm font-medium gap-2" disabled={isSaving} onClick={handleCreate}>
                {isSaving ? t("creating_btn") : s.isCoupon ? t("create_coupon_btn") : t("create_btn")}
                {!isSaving && <ChevronRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Right: Preview */}
          <PreviewPanel state={previewState} />
        </div>
      </div>
    </div>
  );
}
